import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

import { CustomerService, TokenResponse } from 'src/app/services/customer.service';
import { StorageService } from '../services/storage.service';

import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ErrorsInterceptor implements HttpInterceptor {
  private readonly customerService = inject(CustomerService);
  private readonly storageService = inject(StorageService);

  private readonly isRefreshing = new BehaviorSubject<boolean>(false);
  private readonly tokenSubject = new BehaviorSubject<string | null>(null);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && !environment.production) {
          console.error('SHOP ::: intercept HttpErrorResponse', err);
        }

        if (err.status === 404) {
          console.error('This endpoint does not exist: ' + request.url);
        }

        const token = this.storageService.getGlobalItem<string>('token');

        const expiredJwt =
          err.status === 401 &&
          (
            err.error?.message === 'Expired JWT Token' ||
            err.error?.Message === 'Expired JWT Token' ||
            (typeof err.error === 'string' && err.error.includes('Expired JWT Token'))
          );
        if (expiredJwt) {
          this.customerService.removeCustomerData();
          return throwError(() => ({
            status: 401,
            message: 'Expired JWT Token',
          }));
        }

        // Backend may proxy 401s from external services (e.g. Braze) that are unrelated
        // to the user's JWT — skip token refresh/logout for those.
        const isUpstreamError =
          err.status === 401 &&
          typeof err.error?.message === 'string' &&
          err.error.message.includes('returned for');

        if (
          !isUpstreamError &&
          err.status === 401 &&
          request.url.indexOf('login', 0) === -1 &&
          request.url.indexOf('logout', 0) === -1 &&
          request.url.indexOf('api/token/refresh', 0) === -1 &&
          token
        ) {
          return this.handle401Error(request, next);
        }

        let extractedMessage = '';

        if (err.error) {
          if (typeof err.error === 'string') {
            extractedMessage = err.error;
          } else if (typeof err.error === 'object' && err.error.message) {
              extractedMessage = err.error.message;
          }
        }

        if (!extractedMessage) {
          extractedMessage = err.message || 'Unknown error';
        }

        return throwError({
          status: err.status,
          message: extractedMessage
        });
      })
    );
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing.value) {
      this.isRefreshing.next(true);
      this.tokenSubject.next(null);

      const refreshToken = this.storageService.getGlobalItem<string>('refreshToken');
      if (refreshToken) {
        return this.customerService.refreshToken(refreshToken).pipe(
          switchMap((tokenResponse: TokenResponse) => {
            this.tokenSubject.next(tokenResponse.token);
            return next.handle(this.addTokenToRequest(request, tokenResponse.token));
          }),
          catchError((err) => {
            console.error('Failed to refresh token - Logging out');
            this.customerService.logout$().subscribe();
            return throwError(() => err);
          }),
          finalize(() => {
            this.isRefreshing.next(false);
          })
        );
      } else {
        console.error('No refresh token available - Logging out');
        this.customerService.logout$().subscribe();
        return throwError(() => new Error('No refresh token available'));
      }
    } else {
      return this.tokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          if (token) {
            return next.handle(this.addTokenToRequest(request, token));
          } else {
            console.error('Token refresh failed - Logging out');
            this.customerService.logout$().subscribe();
            return throwError(() => new Error('Token is null after refresh'));
          }
        })
      );
    }
  }

  private addTokenToRequest(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
